import {ActionTree, MutationTree} from 'vuex'
import {RootState} from '@/store/types'
import {SERVER_HOST_QR} from '@/config';
import axios from 'axios';


export interface State {
  total: any,
  surveysFilled: any,
  activeStandmans: any,
  createdContacts: any,
  standmansRating: any,
  contactAttributes: { hotLevel: any, category: any },
  productScans: any,
  productByThematics: any,
  standmansContacts: any,
  standmansSurveys: any,
  loading: {
    total: boolean,
    surveysFilled: boolean,
    activeStandmans: boolean,
    createdContacts: boolean,
    standmansRating: boolean,
    contactAttributes: boolean,
    productScans: boolean,
    productByThematics: boolean,
    standmansContacts: boolean,
    standmansSurveys: boolean,
  },
  error: {
    total: boolean,
    surveysFilled: boolean,
    activeStandmans: boolean,
    createdContacts: boolean,
    standmansRating: boolean,
    contactAttributes: boolean,
    productScans: boolean,
    productByThematics: boolean,
    standmansContacts: boolean,
    standmansSurveys: boolean,
  }
}

const defaultState: State = {
  total: {
    allScans: 0,
    scansToday: 0,
    timestampShowStart: null,
    totalChart: []
  },
  surveysFilled: {
    filledAllSurveys: null,
    shardsWithFilledSurveys: null,
    filedSurveysToday: null,
    filledSurveys: [],
  },
  activeStandmans: {
    activeStandmans: 0,
    standmans: []
  },
  createdContacts: {},
  standmansRating: {
    byCount: [],
    byFilling: []
  },
  contactAttributes: {
    hotLevel: {
      none: 0,
      cold: 0,
      warm: 0,
      hot: 0,
    },
    category: {
      isNew: 0,
      exists: 0,
      none: 0,
    }
  },
  productScans: {
    companyScanCount: 0,
    products: []
  },
  productByThematics: [],
  standmansContacts: [],
  standmansSurveys: [],
  loading: {
    total: false,
    surveysFilled: false,
    activeStandmans: false,
    createdContacts: false,
    standmansRating: false,
    contactAttributes: false,
    productScans: false,
    productByThematics: false,
    standmansContacts: false,
    standmansSurveys: false,
  },
  error: {
    total: false,
    surveysFilled: false,
    activeStandmans: false,
    createdContacts: false,
    standmansRating: false,
    contactAttributes: false,
    productScans: false,
    productByThematics: false,
    standmansContacts: false,
    standmansSurveys: false,
  }
}

const getters = {
  total: (state: State) => state.total,
  surveysFilled: (state: State) => state.surveysFilled,
  surveysWithChecked: (state: State) => {
    if (!state.surveysFilled.filledSurveys.length) return []
    return state.surveysFilled.filledSurveys.map((survey: any) => {
      return { ...survey, checked: false }
    })
  },
  activeStandmans: (state: State) => state.activeStandmans,
  createdContacts: (state: State) => state.createdContacts,
  standmansRating: (state: State) => state.standmansRating,
  contactAttributes: (state: State) => state.contactAttributes,
  productScans: (state: State) => state.productScans,
  productByThematics: (state: State) => state.productByThematics,
  standmansContacts: (state: State) => state.standmansContacts,
  standmansSurveys: (state: State) => state.standmansSurveys,
  loading: (state: State) => state.loading,
  error: (state: State) => state.error
}

const mutations: MutationTree<State> = {
  setLoading(state: State, payload) {
    state.loading = {
      ...state.loading,
      ...payload
    }
  },
  setError(state:State, payload: any) {
    state.error = {
      ...state.error,
      ...payload }
  }
}

const actions: ActionTree<State, RootState> = {
  async fetchTotal({commit, state}) {
    commit('setLoading', {total: true})
    commit('setError', {total: false})
    try {
      const {data} = await axios.get(`${SERVER_HOST_QR}statistic/company/stand/count-contact`)
      state.total = data.data
    } catch (e) {
      commit('setError', {total: true})
    } finally {
      commit('setLoading', {total: false})
    }
  },
  async fetchSurveys({commit, state}) {
    commit('setLoading', {surveysFilled: true})
    commit('setError', {surveysFilled: false})
    try {
      const {data} = await axios.get(`${SERVER_HOST_QR}statistic/company/filled-surveys`)
      state.surveysFilled = data.data
    } catch (e) {
      commit('setError', {surveysFilled: true})
    } finally {
      commit('setLoading', {surveysFilled: false})
    }
  },
  async fetchActiveStandmans({commit, state}) {
    commit('setLoading', {activeStandmans: true})
    commit('setError', {activeStandmans: false})
    try {
      const {data} = await axios.get(`${SERVER_HOST_QR}statistic/company/active-standman`)
      state.activeStandmans = data.data
    } catch (e) {
      commit('setError', {activeStandmans: true})
    } finally {
      commit('setLoading', {activeStandmans: false})
    }
  },
  async fetchCreatedContacts({commit, state}) {
    commit('setLoading', {createdContacts: true})
    commit('setError', {createdContacts: false})
    try {
      const {data} = await axios.get(`${SERVER_HOST_QR}statistic/company/created-contacts-surveys`)
      state.createdContacts = data.data
    } catch (e) {
      commit('setError', {createdContacts: true})
    } finally {
      commit('setLoading', {createdContacts: false})
    }
  },
  async fetchStandmansRating({commit, state}) {
    commit('setLoading', {standmansRating: true})
    commit('setError', {standmansRating: false})
    try {
      const {data} = await axios.get(`${SERVER_HOST_QR}statistic/company/standman-rating`)
      state.standmansRating = data.data
    } catch (e) {
      commit('setError', {standmansRating: true})
    } finally {
      commit('setLoading', {standmansRating: false})
    }
  },
  async fetchShardAttributes({commit, state}) {
    commit('setLoading', {contactAttributes: true})
    commit('setError', {contactAttributes: false})
    try {
      const {data} = await axios.get(`${SERVER_HOST_QR}statistic/company/shard/attributes`)
      state.contactAttributes = data.data
    } catch (e) {
      commit('setError', {contactAttributes: true})
    } finally {
      commit('setLoading', {contactAttributes: false})
    }
  },
  async fetchProductScans({commit, state}) {
    commit('setLoading', {productScans: true})
    commit('setError', {productScans: false})
    try {
      const {data} = await axios.get(`${SERVER_HOST_QR}statistic/company/count-scans-products`)
      state.productScans = data.data
    } catch (e) {
      commit('setError', {productScans: true})
    } finally {
      commit('setLoading', {productScans: false})
    }
  },
  async fetchProductByThematics({commit, state}) {
    commit('setLoading', {productByThematics: true})
    commit('setError', {productByThematics: false})
    try {
      const {data} = await axios.get(`${SERVER_HOST_QR}statistic/company/count-scan-products-by-thematics`)
      state.productByThematics = data.data
    } catch (e) {
      commit('setError', {productByThematics: true})
    } finally {
      commit('setLoading', {productByThematics: false})
    }
  },
  async fetchStandmansContacts({commit, state}) {
    commit('setLoading', {standmansContacts: true})
    commit('setError', {standmansContacts: false})
    try {
      const {data} = await axios.get(`${SERVER_HOST_QR}statistic/company/standmans/created-contacts`)
      state.standmansContacts = data.data
    } catch (e) {
      commit('setError', {standmansContacts: true})
    } finally {
      commit('setLoading', {standmansContacts: false})
    }
  },
  async fetchStandmansSurveys({commit, state}) {
    commit('setLoading', {standmansSurveys: true})
    commit('setError', {standmansSurveys: false})
    try {
      const {data} = await axios.get(`${SERVER_HOST_QR}statistic/company/standmans/standmen-contact-survey`)
      state.standmansSurveys = data.data
    } catch (e) {
      commit('setError', {standmansSurveys: true})
    } finally {
      commit('setLoading', {standmansSurveys: false})
    }
  },
  loadAll({ dispatch, state }) {
    if (!state.loading.total) dispatch('fetchTotal')
    if (!state.loading.surveysFilled) dispatch('fetchSurveys')
    if (!state.loading.activeStandmans) dispatch('fetchActiveStandmans')
    if (!state.loading.createdContacts) dispatch('fetchCreatedContacts')
    if (!state.loading.standmansRating) dispatch('fetchStandmansRating')
    if (!state.loading.contactAttributes) dispatch('fetchShardAttributes')
    if (!state.loading.productScans) dispatch('fetchProductScans')
  },
  loadSurveysFromServer({dispatch, state}) {
    if (!state.loading.surveysFilled) dispatch('fetchSurveys')
  },
  loadStandmans({ dispatch, state }) {
    if (!state.loading.standmansContacts) dispatch('fetchStandmansContacts')
    if (!state.loading.standmansSurveys) dispatch('fetchStandmansSurveys')
  },
  loadScannings({ dispatch }) {
    dispatch('fetchProductScans')
    dispatch('fetchProductByThematics')
  },
  loadPdf({ dispatch, state }) {
    if (!state.loading.total) dispatch('fetchTotal')
    if (!state.loading.createdContacts) dispatch('fetchCreatedContacts')
    if (!state.loading.contactAttributes) dispatch('fetchShardAttributes')
    if (!state.loading.standmansRating) dispatch('fetchStandmansRating')
    if (!state.loading.activeStandmans) dispatch('fetchActiveStandmans')
    if (!state.loading.surveysFilled) dispatch('fetchSurveys')
  }
}

export default {
  namespaced: true,
  state: defaultState,
  getters,
  mutations,
  actions
}
